/**
 * Attefall – Sveriges mest sökta bygglovsfråga. 8+ underrubriker.
 */
export const attefallRules = [
  {
    slug: "attefallshus-regler",
    title: "Attefallshus – alla regler 2026",
    h1: "Attefallshus – alla regler för 30 m² attefall",
    metaDescription:
      "Attefallshus 2026: max 30 m² byggnadsarea, 4 m nockhöjd, 4,5 m från tomtgräns. Anmälan + startbesked krävs. Så söker du.",
    keywords: "attefallshus, attefall 30 m², attefall regler 2026, attefall anmälan",
    intro:
      "Sedan 2020 får du bygga ett attefallshus på max 30 m² byggnadsarea utan bygglov – men anmälan och startbesked krävs. Här är alla mått och villkor.",
    verified_source: { sfs: "2010:900", chapter: "9", paragraph: "4" },
    excerpt: {
      text: "Trots kravet på bygglov får man i omedelbar närhet av ett en- eller tvåbostadshus utan bygglov uppföra en eller flera fristående byggnader (attefallshus).",
      source: "PBL 9 kap. 4a §",
    },
    measurements: [
      { label: "Max byggnadsarea", value: "30 m²" },
      { label: "Max nockhöjd", value: "4,0 m" },
      { label: "Min avstånd till tomtgräns", value: "4,5 m (utan grannens medgivande)" },
      { label: "Placering", value: "I omedelbar närhet av huvudbyggnaden" },
      { label: "Ändamål", value: "Komplementbostad eller komplementbyggnad" },
    ],
    examples: [
      { type: "Villa", title: "Gäststuga 25 m²", text: "Två rum + pentry + WC – perfekt storlek för korttidsboende eller uthyrning." },
      { type: "Attefall", title: "Poolhus 20 m²", text: "Omklädningsrum + bastu + förråd – räknas som komplementbyggnad, inte permanent boende." },
    ],
    mistakes: [
      "Att bygga innan startbesked utfärdats – ger byggsanktionsavgift även för attefall.",
      "Att placera huset närmare än 4,5 m från tomtgräns utan skriftligt grannmedgivande.",
      "Att glömma att strandskydd (100 m från vatten) fortfarande gäller trots attefallsreglerna.",
    ],
    municipality_variations:
      "Vissa kommuner har utökat lovplikten via detaljplan i värdefull kulturmiljö – t.ex. i innerstäder eller riksintressen. Kontrollera din detaljplan.",
    faq: [
      { q: "Måste jag söka bygglov för attefallshus?", a: "Nej, men du måste göra anmälan till kommunen och invänta startbesked innan bygget startar." },
      { q: "Hur långt från tomtgräns?", a: "Minst 4,5 m om inte grannen skriftligen godkänner närmare placering. Nära vatten gäller strandskydd." },
      { q: "Får jag ha två attefallshus?", a: "Ja, så länge total byggnadsarea inte överskrider 30 m² per fastighet." },
      { q: "Kan attefallshus vara permanent bostad?", a: "Ja, om det uppfyller BBR-krav (bostadstillstånd) – annars bara komplementbyggnad." },
      { q: "Får jag ansluta vatten och avlopp?", a: "Ja, men det kräver tillstånd från kommunen (avlopp) och VA-huvudmannen (vatten)." },
      { q: "Vad kostar anmälan?", a: "Varierar mellan 3 000–10 000 kr beroende på kommun." },
    ],
    howto: {
      name: "Så bygger du attefallshus lagligt",
      steps: [
        { name: "1. Kolla detaljplan & strandskydd", text: "Är tomten inom detaljplan? Nära vatten? Utökad lovplikt kan gälla." },
        { name: "2. Ta fram ritningar", text: "Situationsplan, planritning, fasadritning i skala 1:100." },
        { name: "3. Anmälan till kommunen", text: "Fyll i anmälan + bifoga ritningar + kontrollplan." },
        { name: "4. Invänta startbesked", text: "Kommunen har 4 veckor på sig att svara." },
        { name: "5. Bygg", text: "När startbesked kommit får du börja bygga." },
        { name: "6. Slutbesked", text: "Kalla på slutbesiktning – utan slutbesked får huset inte tas i bruk." },
      ],
    },
    related: [
      "attefall/attefallstillbyggnad-15m",
      "attefall/attefall-avstand-tomtgrans",
      "attefall/attefall-strandskydd",
      "pbl/9-kap-bygglov-rivningslov-marklov",
    ],
    updated: "2026-07-04",
  },
  {
    slug: "attefallstillbyggnad-15m",
    title: "Attefallstillbyggnad – 15 m² utan bygglov",
    h1: "Attefallstillbyggnad – 15 m² tillbyggnad utan bygglov",
    metaDescription:
      "Bygg en tillbyggnad på max 15 m² utan bygglov via attefallsreglerna. Alla regler, mått och exempel.",
    keywords: "attefall tillbyggnad, 15 m² tillbyggnad, attefall utbyggnad",
    intro:
      "Attefallsreglerna tillåter en tillbyggnad på max 15 m² bruttoarea utan bygglov – men anmälan krävs.",
    measurements: [
      { label: "Max bruttoarea", value: "15 m²" },
      { label: "Max en per bostadshus", value: "Endast en attefallstillbyggnad per fastighet" },
      { label: "Höjd", value: "Får ej vara högre än huvudbyggnadens taknock" },
      { label: "Avstånd till tomtgräns", value: "4,5 m (utan grannens medgivande)" },
    ],
    examples: [
      { type: "Villa", title: "Uterum 14 m²", text: "Isolerat uterum på 14 m² – anmälan räcker, ingen bygglov." },
      { type: "Villa", title: "Utökat vardagsrum 15 m²", text: "Bygg ut vardagsrummet 3 x 5 m – klassisk attefallstillbyggnad." },
    ],
    mistakes: [
      "Att räkna nettoarea istället för bruttoarea – 15 m² gäller BTA, inkl. väggar.",
      "Att göra tillbyggnaden högre än huvudhusets nock – detta är inte tillåtet.",
    ],
    municipality_variations:
      "Precis som för attefallshus kan utökad lovplikt gälla i vissa kulturmiljöer.",
    faq: [
      { q: "Kan jag göra flera attefallstillbyggnader?", a: "Nej, endast en per bostadshus enligt PBL 9 kap. 4b §." },
      { q: "Får den vara i två plan?", a: "Ja, så länge totala bruttoarean är ≤15 m² och den inte överstiger husets höjd." },
    ],
    related: ["attefall/attefallshus-regler", "attefall/attefall-avstand-tomtgrans", "bygglov/tillbyggnad"],
    updated: "2026-07-04",
  },
  {
    slug: "attefall-avstand-tomtgrans",
    title: "Attefall – avstånd till tomtgräns",
    h1: "Attefall – avstånd till tomtgräns och granne",
    metaDescription:
      "4,5-metersregeln vid attefall: när krävs grannmedgivande och hur skriver du det? Praktiska exempel för villaägare.",
    keywords: "attefall tomtgräns, 4,5 meter, grannmedgivande attefall",
    intro:
      "Standardregeln är 4,5 m från tomtgräns – vill du bygga närmare krävs skriftligt medgivande från alla berörda grannar.",
    excerpt: {
      text: "Bygglovsbefriade åtgärder får placeras närmare gränsen än 4,5 meter om de grannar som berörs medger det.",
      source: "PBL 9 kap. 5 § (praxis via prop. 2013/14:127)",
    },
    examples: [
      { type: "Villa", title: "3 m från gräns", text: "Bygga attefallshus 3 m från grannens tomt – kräver skriftligt medgivande från just den grannen." },
      { type: "Attefall", title: "Vid gata", text: "Mot allmän plats (gata/park) räknas kommunen som granne – ansök om avvikelse istället." },
    ],
    mistakes: [
      "Att förlita sig på muntligt medgivande – kräv alltid underskrift på ritning.",
      "Att glömma att kommunen räknas som granne mot gata/park.",
    ],
    municipality_variations:
      "Vid tveksamhet kräver vissa kommuner formellt yttrande från grannen – det räcker inte med signatur på ritning.",
    faq: [
      { q: "Hur skriver jag grannmedgivande?", a: "Skriftligt med grannens namn, fastighetsbeteckning, planerad åtgärd och underskrift. Bifoga situationsplan där placeringen framgår." },
      { q: "Vad händer om grannen säger nej?", a: "Du kan söka bygglov istället – kommunen prövar då åtgärden trots avvikelse från 4,5 m." },
    ],
    related: ["attefall/attefallshus-regler", "bbr/kap-5-brandskydd"],
    updated: "2026-07-04",
  },
  {
    slug: "attefall-strandskydd",
    title: "Attefall och strandskydd",
    h1: "Attefall inom strandskydd – vad gäller?",
    metaDescription:
      "Strandskyddet gäller trots attefallsreglerna. När behöver du dispens och hur söker du?",
    keywords: "attefall strandskydd, dispens strand, attefallshus vid sjö",
    intro:
      "Även om du bygger enligt attefallsreglerna gäller fortfarande strandskyddet – inom 100 m (ibland 300 m) från strandlinjen krävs dispens.",
    excerpt: {
      text: "Inom strandskyddat område får man inte uppföra nya byggnader utan strandskyddsdispens.",
      source: "Miljöbalken 7 kap. 15 §",
    },
    examples: [
      { type: "Attefall", title: "Sjötomt", text: "Attefallshus 40 m från sjöstrand – kräver strandskyddsdispens från länsstyrelsen eller kommunen." },
    ],
    mistakes: [
      "Att tro att 'bygglovsbefriat' också betyder 'strandskyddsbefriat' – dessa är två olika regelverk.",
      "Att bygga inom hemfridszon utan dispens – hemfridszonen är inte större än vad domstolen bedömer skäligt.",
    ],
    municipality_variations:
      "Vissa kommuner har delegation att pröva strandskyddsdispens själva. Andra måste hänvisa till länsstyrelsen.",
    faq: [
      { q: "Krävs alltid dispens?", a: "Nej, om åtgärden ligger inom befintlig hemfridszon (t.ex. runt villan) behövs oftast inte dispens." },
      { q: "Vad är särskilda skäl?", a: "Sex lagliga skäl enligt Miljöbalken 7:18c – bl.a. redan ianspråktaget område och angelägen samhällsintresse." },
    ],
    related: ["strandskydd/100-metersregeln", "attefall/attefallshus-regler", "strandskydd/sarskilda-skal"],
    updated: "2026-07-04",
  },
  {
    slug: "attefall-bygglov-eller-anmalan",
    title: "Attefall – bygglov eller anmälan?",
    h1: "Attefall – bygglov eller anmälan? Så skiljer du dem åt",
    metaDescription:
      "Bygglov kontra anmälan för attefall: så vet du vad som gäller. Handläggningstid, kostnad och startbesked.",
    keywords: "attefall bygglov anmalan skillnad, attefall startbesked",
    intro:
      "Attefall är per definition bygglovsbefriat men kräver anmälan + startbesked. Här förklaras skillnaden.",
    examples: [
      { type: "Attefall", title: "Anmälan", text: "Enkel process – ritningar + kontrollplan + kontrollansvarig (KA) vid större åtgärder." },
      { type: "Villa", title: "Bygglov", text: "Full prövning – kommunen bedömer mot detaljplan, grannintressen och andra lagar." },
    ],
    mistakes: [
      "Att skicka in bygglovsansökan när anmälan räcker – blir dyrare och tar längre tid.",
      "Att starta bygget efter anmälan skickats men innan startbesked – detta är sanktionsbelagt.",
    ],
    municipality_variations:
      "Vissa kommuner har snabbare handläggningstid för anmälan än 4 veckor – ring och fråga innan.",
    faq: [
      { q: "Hur lång tid tar anmälan?", a: "Kommunen ska svara inom 4 veckor från komplett anmälan." },
      { q: "Behöver jag kontrollansvarig?", a: "Ja, för attefallshus med bostadsutrymme. Nej för mindre komplementbyggnader (t.ex. förråd)." },
    ],
    related: ["attefall/attefallshus-regler", "pbl/9-kap-bygglov-rivningslov-marklov"],
    updated: "2026-07-04",
  },
  {
    slug: "komplementbostadshus",
    title: "Komplementbostadshus (Attefallshus som bostad)",
    h1: "Komplementbostadshus – bygga fullvärdig bostad på tomten",
    metaDescription: "Ett attefallshus på 30 m² kan användas som fullvärdig bostad om det uppfyller BBR-kraven för bostad. Regler 2026.",
    keywords: "komplementbostadshus, attefallshus bostad, 30 kvm bostad",
    intro: "Skillnaden mellan attefallshus (komplementbyggnad) och komplementbostadshus är att det senare får användas som självständig, permanent bostad. Kräver strängare BBR-krav.",
    verified_source: { sfs: "2010:900", chapter: "9", paragraph: "4a" },
    measurements: [
      { label: "Max byggnadsarea", value: "30 m²" },
      { label: "Max nockhöjd", value: "4 m från medelmarknivå" },
      { label: "Krävs", value: "Anmälan, startbesked, KA, slutbesked" },
      { label: "BBR-krav", value: "Isolering, ventilation, brand, tillgänglighet på entré" },
    ],
    examples: [
      { type: "Villa", title: "Uthyrning som lägenhet", text: "Bygg 30 m² komplementbostadshus – kräver eget kök, WC/dusch, ventilation." },
      { type: "Villa", title: "Gästhus", text: "Attefallshus utan bostadsfunktion – lägre krav (ingen ventilation krävs)." },
    ],
    mistakes: [
      "Att söka som 'komplementbyggnad' men bo där permanent – kan leda till tillsyn.",
      "Att glömma tillgänglighetsanpassning av entrén – krävs även för komplementbostadshus.",
    ],
    municipality_variations: "Möjligheten att hyra ut som självständig bostad varierar med kommunens uthyrningspolitik.",
    faq: [
      { q: "Får jag hyra ut komplementbostadshus?", a: "Ja – Airbnb och långtidsuthyrning är tillåtet i normalfallet." },
      { q: "Räknas det som andra bostad skattemässigt?", a: "Ja, viktigt att anmäla till Skatteverket för korrekt fastighetsavgift." },
    ],
    related: ["attefall/attefallshus-regler", "bygglov/andrad-anvandning"],
    updated: "2026-07-04",
  },
  {
    slug: "attefall-takkupa",
    title: "Attefall takkupa – 2 kupor utan bygglov",
    h1: "Attefall takkupa – upp till 2 kupor bygglovsfritt",
    metaDescription: "Du får bygga upp till 2 takkupor på ditt en- eller tvåbostadshus utan bygglov, bara anmälan. Regler och mått 2026.",
    keywords: "attefall takkupa, takkupa utan bygglov, 2 takkupor",
    intro: "Enligt PBL 9:4b får du bygga max 2 takkupor på ett en- eller tvåbostadshus utan bygglov, förutsatt att kupan inte ändrar husets bärande konstruktion väsentligt.",
    verified_source: { sfs: "2010:900", chapter: "9", paragraph: "4b" },
    measurements: [
      { label: "Max antal", value: "2 takkupor totalt per bostadshus" },
      { label: "Max bredd", value: "Halva takfallets längd" },
      { label: "Bärande konstruktion", value: "Får inte ändras väsentligt" },
      { label: "Krävs", value: "Anmälan + startbesked (inte bygglov)" },
    ],
    examples: [
      { type: "Villa", title: "2 kupor på gamla huset", text: "Anmälan räcker – ny bostadsyta på övervåningen." },
      { type: "Villa", title: "3:e takkupan", text: "Bygglov krävs eftersom max 2 gäller." },
    ],
    mistakes: [
      "Att bygga takkupa som skär av bärande takstol utan konstruktör – kan orsaka takras.",
      "Att inte göra anmälan i tro att attefall = fritt.",
    ],
    municipality_variations: "Kulturmiljö-skyddade områden (Q, q, k) kräver alltid bygglov även för takkupor.",
    faq: [
      { q: "Räknas nockhöjning som takkupa?", a: "Nej, det är ombyggnad och kräver bygglov." },
      { q: "Får jag glasa in kupan?", a: "Ja, om detaljplan tillåter större fönsterareor." },
    ],
    related: ["attefall/attefallstillbyggnad-15m", "bygglov/tillbyggnad"],
    updated: "2026-07-04",
  },
  {
    slug: "attefall-i-detaljplan",
    title: "Attefall trots detaljplan?",
    h1: "Attefall i detaljplan – går det?",
    metaDescription: "Attefallsreglerna gäller även om detaljplan säger annat – men kulturmiljöskydd (Q) och strandskydd är undantag. Regler 2026.",
    keywords: "attefall detaljplan, attefall trots plan, Q-märkt attefall",
    intro: "Attefallsreglerna (PBL 9:4a-c) gäller överordnat detaljplan – du får bygga även om planen säger tvärtom. Men kulturmiljöskydd (Q, q, k) och strandskydd är undantag där attefall INTE gäller.",
    verified_source: { sfs: "2010:900", chapter: "9", paragraph: "4a" },
    measurements: [
      { label: "Attefall gäller trots plan", value: "Ja – i normalfallet" },
      { label: "Attefall gäller INTE", value: "Q/q/k-planer (kulturmiljö), strandskydd" },
      { label: "Prickmark", value: "Du får bygga attefall även på prickmark" },
    ],
    examples: [
      { type: "Villa", title: "Detaljplan säger max 100 m² BYA", text: "Attefall 30 m² tillåts ändå ovanpå – planen slår ej mot attefall." },
      { type: "Villa", title: "Q-märkt trähus", text: "Attefall gäller INTE – bygglov krävs för allt." },
    ],
    mistakes: [
      "Att tro att attefall alltid gäller – kolla Q/q/k-beteckningar i planen.",
      "Att bygga attefall inom strandskyddat område utan dispens.",
    ],
    municipality_variations: "Innerstadsområden i storstäder har oftare kulturmiljöskydd som blockerar attefall.",
    faq: [
      { q: "Kan kommunen neka attefall pga att det är fult?", a: "Nej – estetisk bedömning görs inte för attefall utanför kulturmiljö." },
    ],
    related: ["attefall/attefallshus-regler", "detaljplan/vad-ar-en-detaljplan", "attefall/attefall-strandskydd"],
    updated: "2026-07-04",
  },
];
